import React from 'react'
import i18next from 'i18next'
import {Col, Row, Tooltip} from 'antd'
import {isEmpty} from 'dna-common'

import {CardSchemeLabelWithIcon, DateTimeLabel, Icon, Status} from '@/components'
import {Description} from '@/components/dumb/Description/props'
import {DEFAULT_CURRENCY} from '@/constants/currencies'
import {
    TransactionDetailsItemType,
    BasePOSTransactionType,
    POSTransactionType
} from '@/types/transactions'
import {getAmount, getAmountWithCurrency, numberWithCommas, round} from '@/utils'

import {
    getPOSPaymentStatusName,
    getPOSPaymentStatusTagType,
    getPOSPaymentStatusIconType,
    getPOSTransactionType
} from '.'
import styles from './styles.scss'

export const orderMainDetails = (
    transaction: BasePOSTransactionType
): TransactionDetailsItemType => ({
    title: i18next.t('pos-transaction.paymentDetails'),
    fields: [
        {
            label: i18next.t('pos-transaction.amount'),
            value: getAmountWithCurrency(transaction.amount, transaction.currency)
        },
        {
            label: i18next.t('pos-transaction.date'),
            value: `${transaction.transactionDate}`,
            render: () => <DateTimeLabel date={transaction?.transactionDate} />
        },
        {
            label: i18next.t('pos-transaction.status'),
            value: getPOSPaymentStatusName(transaction.status),
            render: () => (
                <Row>
                    <Col>
                        <Status
                            status={getPOSPaymentStatusName(transaction.status)}
                            type={getPOSPaymentStatusTagType(transaction.status)}
                            iconType={getPOSPaymentStatusIconType(transaction.status)}
                        />
                    </Col>
                </Row>
            )
        },
        {
            label: i18next.t('pos-transaction.transaction.id'),
            value: transaction.id,
            copyable: true
        },
        {
            label: i18next.t('pos-transaction.transaction.type'),
            value: getPOSTransactionType(transaction)
        }
    ]
})

export const orderSecondaryDetails = (
    transaction: BasePOSTransactionType,
    newFields?: Description<string>[],
    fieldsIndex?: number
): TransactionDetailsItemType => {
    const fields = [
        {
            key: 'transactionDetails',
            label: i18next.t('pos-transaction.transaction.details'),
            value: transaction.transactionDetails
        },
        {
            key: 'mid',
            label: i18next.t('pos-transaction.mid'),
            value: transaction.mid
        },
        {
            key: 'terminalId',
            label: i18next.t('pos-transaction.terminalId'),
            value: transaction.terminalId,
            copyable: true
        },
        {
            key: 'resultCode',
            label: i18next.t('pos-transaction.resultCode'),
            value: transaction.returnCode
        },
        {
            key: 'description',
            label: i18next.t('pos-transaction.message'),
            value: transaction.description
        },
        {
            key: 'cardMask',
            label: i18next.t('pos-transaction.card'),
            value: transaction.cardMask,
            render: () => (
                <CardSchemeLabelWithIcon
                    cardScheme={transaction.cardScheme}
                    title={transaction.cardMask}
                />
            ),
            hidden: !transaction.cardMask
        },
        {
            key: 'captureMethod',
            label: i18next.t('pos-transaction.captureMethod'),
            value: transaction.captureMethod
                ? i18next.t(`enums:captureMethods.${transaction.captureMethod}`, {
                      defaultValue: transaction.captureMethod
                  })
                : ''
        },
        {
            key: 'transactionCountry',
            label: i18next.t('pos-transaction.transaction.country'),
            value: transaction.transactionCountry
        },
        {
            key: 'transactionCity',
            label: i18next.t('pos-transaction.transaction.city'),
            value: transaction.transactionCity
        }
    ]

    if (newFields) {
        return {
            title: i18next.t('pos-transaction.details'),
            fields: [...fields.slice(0, fieldsIndex), ...newFields, ...fields.slice(fieldsIndex)]
        }
    }

    return {title: i18next.t('pos-transaction.details'), fields}
}

export const orderPOSSecondaryDetails = (transaction: POSTransactionType) => {
    return orderSecondaryDetails(
        transaction,
        [
            {
                key: 'cardType',
                label: i18next.t('pos-transaction.cardType'),
                value: transaction?.cardType
                    ? i18next.t(`enums:cardType.${transaction?.cardType}`, {
                          defaultValue: transaction?.cardType
                      })
                    : '',
                hidden: !transaction?.cardType
            },
            {
                key: 'cardCategory',
                label: i18next.t('pos-transaction.cardCategory'),
                value: (() => {
                    switch (transaction?.isCorporateCard) {
                        case true:
                            return i18next.t('pos-transaction.corporate')
                        case false:
                            return i18next.t('pos-transaction.personal')
                        default:
                            return ''
                    }
                })(),
                hidden: isEmpty(transaction?.isCorporateCard)
            },
            {
                key: 'issuerRegion',
                label: i18next.t('pos-transaction.issuerRegion'),
                value: transaction?.issuerRegion
                    ? i18next.t(`enums:issuerRegion.${transaction?.issuerRegion}`, {
                          defaultValue: transaction?.issuerRegion
                      })
                    : '',
                hidden: !transaction?.issuerRegion
            }
        ],
        6
    )
}

export const transactionDccDetails = (
    transaction: POSTransactionType
): TransactionDetailsItemType => {
    return {
        title: i18next.t('pos-transaction.dcc.title'),
        fields: [
            {
                key: 'cardholderCurrency',
                label: i18next.t('pos-transaction.dcc.cardholderCurrency'),
                value:
                    transaction.dcc?.cardholderCurrency &&
                    String(transaction.dcc.cardholderCurrency)
            },
            {
                key: 'cardholderAmount',
                label: i18next.t('pos-transaction.dcc.cardholderAmount'),
                value: String(transaction.dcc?.cardholderAmount),
                render: () => transaction.dcc?.cardholderAmount && transaction.dcc?.cardholderAmount
            },
            {
                key: 'exchangeRate',
                label: i18next.t('pos-transaction.dcc.exchangeRate'),
                value: String(transaction.dcc?.exchangeRate),
                render: () => {
                    return (
                        transaction.dcc?.exchangeRate &&
                        `1 ${transaction.dcc?.rebateCurrency} = ${numberWithCommas(round(getAmount(transaction.dcc.exchangeRate)))} ${transaction.dcc?.cardholderCurrency}`
                    )
                }
            },
            {
                key: 'rebateRate',
                label: i18next.t('pos-transaction.dcc.rebateRate'),
                value: String(transaction.dcc?.rebateRate),
                render: () => (transaction.dcc?.rebateRate ? transaction.dcc?.rebateRate : '0%')
            },
            {
                key: 'rebateAmount',
                label: i18next.t('pos-transaction.dcc.rebateAmount'),
                value: String(transaction.dcc?.rebateAmount),
                render: () => {
                    const amount = transaction.dcc?.rebateAmount
                    const currency = transaction.dcc?.rebateCurrency

                    if (typeof amount === 'number' && Number.isFinite(amount)) {
                        return `${currency} ${amount}`
                    }

                    return (
                        <div className={styles.rebateAmount}>
                            <div>{i18next.t('pos-transaction.dcc.processing')}</div>
                            <div>
                                <Tooltip
                                    title={i18next.t('pos-transaction.dcc.rebateAmountTooltip')}
                                >
                                    <Icon type={'info'} />
                                </Tooltip>
                            </div>
                        </div>
                    )
                }
            }
        ]
    }
}
